import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Clock, AlertTriangle, Hourglass, ListChecks, Check, Square, StickyNote, CircleAlert, Phone, Info, CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useEndShift, useChecklist, useOpenShift, useUpdateShiftNotes } from "../hooks";
import { useTasks } from "@/features/tasks/hooks";
import { useToast } from "@/hooks/use-toast";
import { ShiftTimesSection } from "../components/ShiftTimesSection";
import { LocationCheckCard } from "../components/LocationCheckCard";
import { getCurrentPosition } from "@/shared/utils/location";
import type { Coords } from "@/shared/utils/location";
import type { Task } from "@/features/tasks/types";
import {
  isOvernight,
  isExtendedShift,
  manualEndToISO,
  isManualEndWithin12Hours,
  formatShiftStartDate,
  todayDateString,
  dateToTimeString,
  dateToDateString,
  twelveHoursFromStart,
} from "../utils/shiftEndUtils";
import { isWithinOneMinuteOfNow } from "../utils/manualTimeUtils";
import { NativeTimeInput } from "@/components/ui/native-time-input";

export function ConfirmEndShiftPage() {
  const navigate = useNavigate();
  const endShiftMutation = useEndShift();
  const updateNotesMutation = useUpdateShiftNotes();
  const { data: openShift } = useOpenShift();
  // Check if shift has checklist data
  const hasShiftChecklist = openShift?.end_of_shift_checklist && openShift.end_of_shift_checklist.length > 0;
  // Only fetch checklist from employee endpoint if shift doesn't have one
  const { data: checklistData, isLoading: isLoadingChecklist } = useChecklist({
    enabled: !hasShiftChecklist,
  });
  const { data: tasksData } = useTasks({ status: "pending" });
  const { toast } = useToast();
  const [notes, setNotes] = useState("");
  const [manualEndTime, setManualEndTime] = useState<string>("");
  const [manualEndDate, setManualEndDate] = useState<string>("");
  /** When true and shift is not extended, the manual end card is shown (always shown for extended shifts). */
  const [manualEndSectionOpenByUser, setManualEndSectionOpenByUser] = useState(false);
  const [checklist, setChecklist] = useState<Array<{ item: string; completed: boolean }>>([]);
  const lastShiftNotesRef = useRef<string | null>(null);
  const manualEndSectionRef = useRef<HTMLElement | null>(null);
  const [locationCoords, setLocationCoords] = useState<Coords | null>(null);
  const [locationLoading, setLocationLoading] = useState(true);

  // Use checklist from shift if available, otherwise use employee endpoint data
  const checklistItems = hasShiftChecklist && openShift?.end_of_shift_checklist
    ? openShift.end_of_shift_checklist.map(item => item.item)
    : (checklistData?.checklist || []);
  const shiftData = hasShiftChecklist && openShift
    ? {
        started_at: openShift.started_at,
        manual_arrival_time: openShift.manual_arrival_time,
        arrival_time_approved_by: openShift.arrival_time_approved_by,
      }
    : checklistData?.shift;

  // Initialize and update notes from shift if available
  useEffect(() => {
    if (openShift?.notes !== undefined) {
      // Update notes if:
      // 1. Notes are currently empty (initial load), OR
      // 2. Shift notes have changed from what we last loaded (e.g., after saving and refetching)
      const shouldUpdate = notes === "" || 
        (openShift.notes !== lastShiftNotesRef.current && lastShiftNotesRef.current !== null);
      
      if (shouldUpdate) {
        setNotes(openShift.notes || "");
        lastShiftNotesRef.current = openShift.notes;
      }
    }
  }, [openShift?.notes, notes]);

  // Fetch current location for Location Check card
  useEffect(() => {
    getCurrentPosition().then((coords) => {
      setLocationCoords(coords ?? null);
      setLocationLoading(false);
    });
  }, []);

  // Initialize checklist when items are loaded
  useEffect(() => {
    if (checklistItems.length > 0 && checklist.length === 0) {
      if (hasShiftChecklist && openShift?.end_of_shift_checklist) {
        // Use existing checklist from shift with current completion status
        setChecklist(openShift.end_of_shift_checklist);
      } else {
        // Initialize new checklist from employee endpoint
        setChecklist(
          checklistItems.map((item) => ({
            item,
            completed: false,
          }))
        );
      }
    }
  }, [checklistItems, checklist.length, hasShiftChecklist, openShift?.end_of_shift_checklist]);

  // Format times for display
  const loggedStartTime = useMemo(() => {
    if (!shiftData?.started_at) return "";
    const date = new Date(shiftData.started_at);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }, [shiftData?.started_at]);

  const actualArrivalTime = useMemo(() => {
    // Use manual_arrival_time if available, otherwise default to started_at
    const timeToUse = shiftData?.manual_arrival_time || shiftData?.started_at;
    if (!timeToUse) return "";
    const date = new Date(timeToUse);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }, [shiftData?.manual_arrival_time, shiftData?.started_at]);

  // Shift start and current time for 12h / overnight logic
  const shiftStartDate = useMemo(() => {
    const startedAt = openShift?.started_at ?? shiftData?.started_at;
    return startedAt ? new Date(startedAt) : null;
  }, [openShift?.started_at, shiftData?.started_at]);

  const now = useMemo(() => new Date(), []);

  const { isExtended: isExtendedShiftDetected, isOvernight: showDateInCurrentTimeBox } = useMemo(() => {
    if (!shiftStartDate) return { isExtended: false, isOvernight: false };
    const overnight = isOvernight(shiftStartDate, now);
    const extended = isExtendedShift(shiftStartDate, now);
    return { isExtended: extended, isOvernight: overnight };
  }, [shiftStartDate, now]);

  const manualEndExpanded = isExtendedShiftDetected || manualEndSectionOpenByUser;

  const openManualEndFromFooter = () => {
    setManualEndSectionOpenByUser(true);
    window.setTimeout(() => {
      const el = manualEndSectionRef.current;
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 0);
  };

  // Current time display; include date in current time box when end date differs from start (overnight)
  const currentTime = useMemo(() => {
    return now.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }, [now]);

  const currentTimeDateFormatted = useMemo(() => {
    if (!showDateInCurrentTimeBox || !shiftStartDate) return undefined;
    return now.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }, [showDateInCurrentTimeBox, now, shiftStartDate]);

  const shiftStartDateFormatted = useMemo(() => {
    const startedAt = openShift?.started_at ?? shiftData?.started_at;
    return startedAt ? formatShiftStartDate(startedAt) : "";
  }, [openShift?.started_at, shiftData?.started_at]);

  // Default manual end time/date only when the section is visible (extended shift always; short shift after "Add").
  useEffect(() => {
    if (!shiftStartDate) return;
    if (!manualEndExpanded) return;
    if (manualEndTime || manualEndDate) return;

    if (isExtendedShiftDetected) {
      const twelveHoursLater = twelveHoursFromStart(shiftStartDate);
      setManualEndTime(dateToTimeString(twelveHoursLater));
      setManualEndDate(dateToDateString(twelveHoursLater));
    } else {
      setManualEndTime(dateToTimeString(now));
    }
  }, [
    shiftStartDate,
    isExtendedShiftDetected,
    now,
    manualEndExpanded,
    manualEndTime,
    manualEndDate,
  ]);

  // Build ISO for manual end: use date+time when extended, else time-only (current day)
  const getManualEndTimeISO = (): string | undefined => {
    if (isExtendedShiftDetected && manualEndDate && manualEndTime) {
      return manualEndToISO(manualEndDate, manualEndTime);
    }
    if (manualEndTime && !isExtendedShiftDetected) {
      const [hours, minutes] = manualEndTime.split(":").map(Number);
      const date = new Date();
      date.setHours(hours, minutes, 0, 0);
      return date.toISOString();
    }
    return undefined;
  };

  const hasValidManualEndTime = useMemo(() => {
    if (!isExtendedShiftDetected) return true;
    if (!manualEndDate || !manualEndTime) return false;
    if (!shiftStartDate) return false;
    return isManualEndWithin12Hours(shiftStartDate, manualEndDate, manualEndTime);
  }, [isExtendedShiftDetected, manualEndDate, manualEndTime, shiftStartDate]);

  const canConfirmEndShift = !isExtendedShiftDetected || hasValidManualEndTime;

  const quickNoteOptions = [
    "Productive Shift",
    "Busy Period",
    "Minor Issue",
    "Team Handover",
  ];

  const handleQuickNoteClick = (option: string) => {
    if (notes.trim() === "") {
      setNotes(option);
    } else {
      setNotes((prev) => `${prev}${prev.endsWith(".") ? " " : ". "}${option}`);
    }
  };

  const handleChecklistToggle = (index: number) => {
    setChecklist((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, completed: !item.completed } : item
      )
    );
  };

  const handleConfirm = async () => {
    if (endShiftMutation.isPending || !canConfirmEndShift) return;

    try {
      const coords = await getCurrentPosition();
      const manualEndTimeISO = getManualEndTimeISO();
      const omitManualTime =
        manualEndTimeISO != null && isWithinOneMinuteOfNow(manualEndTimeISO);
      const checklistToSend = checklist.length > 0 ? checklist : undefined;

      await endShiftMutation.mutateAsync({
        notes: notes.trim() || undefined,
        manualEndTime: omitManualTime ? undefined : manualEndTimeISO,
        endOfShiftChecklist: checklistToSend,
        coords,
      });
      toast({
        title: "Shift ended",
        description: "Your shift has been ended successfully.",
      });
      navigate("/shifts");
    } catch (error) {
      toast({
        title: "Failed to end shift",
        description: error instanceof Error ? error.message : "An error occurred",
        variant: "destructive",
      });
    }
  };

  const handleSave = async () => {
    if (!openShift || updateNotesMutation.isPending) return;

    try {
      const checklistToSend = checklist.length > 0 ? checklist : undefined;
      await updateNotesMutation.mutateAsync({
        shiftId: openShift.id,
        notes: notes.trim(),
        endOfShiftChecklist: checklistToSend,
      });
      toast({
        title: "Notes saved",
        description: "Your shift notes and checklist have been saved successfully.",
      });
      // Don't navigate away - stay on the page
    } catch (error) {
      toast({
        title: "Failed to save notes",
        description: error instanceof Error ? error.message : "An error occurred",
        variant: "destructive",
      });
    }
  };

  // Get incomplete tasks that were due during the shift
  const incompleteTasks = useMemo(() => {
    if (!openShift?.started_at || !tasksData?.pages) return [];
    
    const shiftStart = new Date(openShift.started_at);
    const now = new Date();
    
    const allTasks: Task[] = tasksData.pages.flatMap(page => page.data || []);
    
    return allTasks.filter(task => {
      // Check if task has a direct dueAt that falls within shift time
      if (task.dueAt) {
        const dueDate = new Date(task.dueAt);
        if (dueDate >= shiftStart && dueDate <= now) {
          return true;
        }
      }
      
      // Check task instances for due dates within shift time
      if (task.instances && task.instances.length > 0) {
        return task.instances.some(instance => {
          if (instance.status === "pending" && instance.due_date) {
            const dueDate = new Date(instance.due_date);
            return dueDate >= shiftStart && dueDate <= now;
          }
          return false;
        });
      }
      
      return false;
    });
  }, [openShift?.started_at, tasksData]);

  return (
    <div className="flex flex-col h-full bg-gray-50">
      <div className="flex-1 overflow-y-auto">
        {/* Shift Times Section - Blue Background */}
        <ShiftTimesSection
          loggedStartTime={loggedStartTime}
          actualArrivalTime={actualArrivalTime}
          manualArrivalTime={shiftData?.manual_arrival_time}
          arrivalTimeApprovedBy={shiftData?.arrival_time_approved_by}
          currentTime={currentTime}
          currentTimeDate={currentTimeDateFormatted}
        />

        {/* Extended Shift Detected */}
        {isExtendedShiftDetected && (
          <section className="mx-4 mb-0 mt-4">
            <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg flex gap-3 items-center">
              <Clock className="h-5 w-5 text-orange-600 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-orange-900 text-sm mb-0.5">Extended Shift Detected</h4>
                <p className="text-sm text-orange-800">
                  This shift exceeds 12 hours. Please verify the end date and time below to ensure accurate logging.
                </p>
              </div>
            </div>
          </section>
        )}

        {/* Manual End Time — original placement; for short shifts, opened via footer link */}
        {manualEndExpanded && (
          <section
            ref={manualEndSectionRef}
            className="bg-white rounded-xl m-4 p-4 border-gray-200 border"
          >
            <div className="flex items-center justify-between mb-3 gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <Hourglass className="h-5 w-5 shrink-0 text-red-600" />
                <h2 className="text-lg mt-2 text-gray-900">Manual End Time</h2>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {isExtendedShiftDetected ? (
                  <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full font-medium">
                    Required
                  </span>
                ) : (
                  <>
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                      Optional
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setManualEndSectionOpenByUser(false);
                        setManualEndTime("");
                        setManualEndDate("");
                      }}
                      className="text-sm font-medium text-blue-600 hover:text-blue-800"
                    >
                      Hide
                    </button>
                  </>
                )}
              </div>
            </div>

            {isExtendedShiftDetected && (
              <>
                <div className="mb-3">
                  <label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
                  <div className="relative flex items-center overflow-hidden rounded-xl border-2 border-teal-400 bg-white focus-within:ring-2 focus-within:ring-teal-500/30 focus-within:border-teal-500">
                    <input
                      type="date"
                      value={manualEndDate}
                      onChange={(e) => setManualEndDate(e.target.value)}
                      className="w-full flex-1 min-w-0 pl-4 py-3 pr-12 text-base font-medium text-gray-900 bg-transparent border-0 focus:outline-none focus:ring-0 [color-scheme:light]"
                    />
                    <div className="absolute right-3 pointer-events-none text-teal-600">
                      <CalendarIcon className="h-5 w-5" strokeWidth={2} />
                    </div>
                  </div>
                  {shiftStartDateFormatted && (
                    <p className="flex items-center gap-1.5 mt-1.5 text-xs text-gray-500">
                      <Info className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      Shift started: {shiftStartDateFormatted}
                    </p>
                  )}
                </div>
              </>
            )}

            <div className={isExtendedShiftDetected ? "mt-1" : ""}>
              {isExtendedShiftDetected ? (
                <label className="block text-xs font-medium text-gray-600 mb-1" htmlFor="manual-end-time-input">
                  Time
                </label>
              ) : null}
              <NativeTimeInput
                id="manual-end-time-input"
                value={manualEndTime}
                onChange={setManualEndTime}
                aria-label="Manual end time"
                aria-invalid={
                  isExtendedShiftDetected &&
                  !hasValidManualEndTime &&
                  (!!manualEndDate || !!manualEndTime)
                }
                data-testid="manual-end-time"
              />
            </div>

            <div className="mt-3 p-3 bg-yellow-50 border border-orange-200 rounded-lg">
              <div className="flex gap-3 items-center">
                <div className="flex-shrink-0 flex items-center">
                  <AlertTriangle className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-orange-900 text-sm mb-1 mt-1">
                    Approval Required
                  </h4>
                  <p className="text-sm text-orange-800">
                    {isExtendedShiftDetected
                      ? "Extended shifts and manual end times require manager approval before being finalized."
                      : "If you are setting a manual end time your manager will need to approve it."}
                  </p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* End of Shift Checklist Section */}
        {checklist.length > 0 && (
          <section className="bg-white rounded-xl m-4 p-4 border-gray-200 border">
            <div className="flex items-center gap-2">
              <div className="h-5 w-5 rounded flex items-center justify-center">
                <ListChecks className="text-primary" />
              </div>
              <h2 className="text-sm font-semibold text-gray-700 my-1 flex items-center gap-2">Shift Completion</h2>
            </div>
            <div className="space-y-3">
              {checklist.map((item, index) => (
                <label
                  key={index}
                  className="flex items-center gap-3 p-2 !mt-1 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <div className="flex-shrink-0">
                    {item.completed ? (
                      <div className="h-6 w-6 bg-purple-600 rounded flex items-center justify-center">
                        <Check className="h-4 w-4 text-white" />
                      </div>
                    ) : (
                      <Square className="h-6 w-6 text-gray-400" />
                    )}
                  </div>
                  <span
                    className={`flex-1 text-gray-900 ${
                      item.completed ? "line-through text-gray-500" : ""
                    }`}
                  >
                    {item.item}
                  </span>
                  <input
                    type="checkbox"
                    checked={item.completed}
                    onChange={() => handleChecklistToggle(index)}
                    className="sr-only"
                  />
                </label>
              ))}
            </div>
          </section>
        )}

        {/* Incomplete Tasks Section */}
        {incompleteTasks.length > 0 && (
          <section className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 m-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2 m-0">
                <CircleAlert className="h-4 w-4 text-orange-500" />
                Incomplete Tasks
              </h3>
              <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full font-bold">
                {incompleteTasks.length} Pending
              </span>
            </div>
            
            <div className="space-y-3 mb-4">
              {incompleteTasks.map((task) => (
                <div key={task.id} className="border border-gray-200 bg-gray-50 rounded-xl p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-800">{task.title}</p>
                      {task.description && (
                        <p className="text-xs text-gray-600 mt-1">{task.description}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-3 items-start">
              <Phone className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-semibold text-red-800 mb-1">Urgent Issues</p>
                <p className="text-xs text-red-700 leading-relaxed">
                  Any urgent or guest-impacting incomplete tasks must be escalated by phone immediately to your shift supervisor.
                </p>
              </div>
            </div>
          </section>
        )}

        {/* Shift Notes Section */}
        <section className="bg-white rounded-xl m-4 p-4 border-gray-200 border">
          <div className="flex items-center gap-2 mb-3">
              <div className="h-5 w-5 rounded flex items-center justify-center">
                <StickyNote className="text-primary" />
              </div>
              <h2 className="text-sm font-semibold text-gray-700 my-1 flex items-center gap-2 m-0">Shift Notes & Summary</h2>
          </div>
          <Textarea
            placeholder="Add notes about your shift, incidents, handover information, or anything noteworthy..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 placeholder-gray-400 resize-none mb-3"
          />
          <div className="mb-2">
            <p className="text-xs font-semibold text-gray-700 uppercase mb-2">Quick Notes</p>
            <div className="flex flex-wrap gap-2">
              {quickNoteOptions.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => handleQuickNoteClick(option)}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Location Check — header + status only (no map) */}
        <LocationCheckCard
          coords={locationCoords}
          address={null}
          loading={locationLoading}
          showMap={false}
          geofencingNoticeVariant="end"
        />
      </div>


      {/* Action Buttons */}
      <section className="px-4 pb-6 space-y-3 bg-white border-t border-gray-200 pt-4">
        <Button
          onClick={handleConfirm}
          disabled={endShiftMutation.isPending || !canConfirmEndShift}
          className="w-full flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold py-4 rounded-lg h-14 disabled:opacity-60"
        >
          <div className="h-5 w-5 border-2 border-white rounded flex items-center justify-center">
            <div className="h-2 w-2 bg-white rounded"></div>
          </div>
          <span>Confirm End Shift</span>
          <ArrowRight className="h-5 w-5" />
        </Button>
        <Button
          onClick={handleSave}
          disabled={updateNotesMutation.isPending}
          className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 rounded-lg h-14"
        >
          <span>Save</span>
          <ArrowRight className="h-5 w-5" />
        </Button>
        {!manualEndExpanded && (
          <button
            type="button"
            onClick={openManualEndFromFooter}
            className="w-full text-center text-sm font-medium text-blue-600 hover:text-blue-800 underline-offset-4 hover:underline py-1 pt-2 bg-transparent border-0 cursor-pointer"
          >
            Add manual end time
          </button>
        )}
      </section>
    </div>
  );
}

